import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { ServerMessagesService } from './server-messages.service';
import { TopicsEnum } from './constants';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  UliCreatedAndAllocatedServerMessageModel,
  UliCreateServerMessageModel,
  NotaFiscalServerMessageModel,
} from './models/server-messages.model';

import {
  ServerMessagesHubTopic,
  ServerMessagesLoadTopic,
  ServerMessagesDisconnect,
  ServerMessagesInitialState,
  ServerMessagesRemoveAllocateUliAfterCreationMessage,
  ServerMessagesUnsubscribeFromTopic,
  ServerMessagesSetUpConnection,
  ServerMessagesUliTopic,
  ServerMessagesRemoveUliAutoAllocationMessage,
  ServerMessagesNotaFiscalTopic,
} from './server-messages.actions';
import { CustomHttpErrorResponse, Error } from '../../../../../common/constants/error';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface ServerMessagesStateModel {
  highlightedUli: UliCreateServerMessageModel;
  loadTopicMessages: UliCreateServerMessageModel[];
  uliTopicMessages: UliCreatedAndAllocatedServerMessageModel[];
  notaFiscalMessage: NotaFiscalServerMessageModel | null;
}

const initialState: ServerMessagesStateModel = {
  highlightedUli: null,
  loadTopicMessages: [],
  uliTopicMessages: [],
  notaFiscalMessage: null,
};

@State<ServerMessagesStateModel>({
  name: 'ServerMessages',
  defaults: initialState,
})
@Injectable()
export class ServerMessagesState {
  constructor(private serverMessagesService: ServerMessagesService, private notificationService: NotificationService) {}

  @Selector()
  static getState(state: ServerMessagesStateModel): ServerMessagesStateModel {
    return state;
  }

  @Selector()
  static getHighlightedUli(state: ServerMessagesStateModel): UliCreateServerMessageModel {
    return state.highlightedUli;
  }

  @Selector()
  static getLoadTopicMessages(state: ServerMessagesStateModel): UliCreateServerMessageModel[] {
    return state.loadTopicMessages;
  }

  @Selector()
  static getUliTopicMessages(state: ServerMessagesStateModel): UliCreatedAndAllocatedServerMessageModel[] {
    return state.uliTopicMessages;
  }

  @Selector()
  static getNotaFiscalMessage(state: ServerMessagesStateModel): NotaFiscalServerMessageModel | null {
    return state.notaFiscalMessage;
  }

  @Selector()
  static getTotalUnreadMessagesNumber(state: ServerMessagesStateModel): number {
    return state.loadTopicMessages?.length ?? 0;
  }

  @Action(ServerMessagesSetUpConnection)
  setUpConfig(_ctx: StateContext<ServerMessagesStateModel>): void {
    this.serverMessagesService.setUpConnection();
  }

  @Action(ServerMessagesDisconnect)
  disconnect(): void {
    this.serverMessagesService.disconnect();
  }

  @Action(ServerMessagesHubTopic)
  subscribeToHubTopic(
    ctx: StateContext<ServerMessagesStateModel>,
    { payload }: ServerMessagesHubTopic,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.serverMessagesService.subscribeToTopic(TopicsEnum.Hub, payload).pipe(
      map((highlightedUli: UliCreateServerMessageModel) => {
        ctx.patchState({
          highlightedUli,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ServerMessagesLoadTopic)
  subscribeToLoadTopic(
    ctx: StateContext<ServerMessagesStateModel>,
    { payload }: ServerMessagesLoadTopic,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.serverMessagesService.subscribeToTopic(TopicsEnum.Load, payload).pipe(
      map((uli: UliCreateServerMessageModel) => {
        const loadTopicMessages = [...ctx.getState().loadTopicMessages];
        loadTopicMessages.push(uli);
        ctx.patchState({
          loadTopicMessages,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ServerMessagesUliTopic)
  subscribeToUliTopic(
    ctx: StateContext<ServerMessagesStateModel>,
    { payload }: ServerMessagesUliTopic,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.serverMessagesService.subscribeToTopic(TopicsEnum.Uli, payload).pipe(
      map((uli: UliCreatedAndAllocatedServerMessageModel) => {
        const uliTopicMessages = [...ctx.getState().uliTopicMessages];
        uliTopicMessages.push(uli);
        ctx.patchState({
          uliTopicMessages,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ServerMessagesNotaFiscalTopic)
  subscribeToNotaFiscalTopic(
    ctx: StateContext<ServerMessagesStateModel>,
    { payload }: ServerMessagesNotaFiscalTopic,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.serverMessagesService
      .subscribeToTopic<NotaFiscalServerMessageModel>(TopicsEnum.NotaFiscal, payload)
      .pipe(
        map((message: NotaFiscalServerMessageModel) => {
          ctx.patchState({
            notaFiscalMessage: message,
          });
        }),
        catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
      );
  }

  @Action(ServerMessagesUnsubscribeFromTopic)
  unsubscribeFromTopic(
    ctx: StateContext<ServerMessagesStateModel>,
    { topic }: ServerMessagesUnsubscribeFromTopic,
  ): void {
    this.serverMessagesService.unsubscribe(topic);

    if (topic === TopicsEnum.NotaFiscal) {
      ctx.patchState({
        notaFiscalMessage: null,
      });
    }
  }

  @Action(ServerMessagesRemoveAllocateUliAfterCreationMessage)
  removeAllocateUliAfterCreationMessage(
    ctx: StateContext<ServerMessagesStateModel>,
    { payload }: ServerMessagesRemoveAllocateUliAfterCreationMessage,
  ): void {
    const uliMessages: UliCreateServerMessageModel[] = ctx
      .getState()
      .loadTopicMessages.filter((item: UliCreateServerMessageModel) => item.uli !== payload);

    ctx.patchState({ loadTopicMessages: uliMessages, highlightedUli: null });
  }

  @Action(ServerMessagesRemoveUliAutoAllocationMessage)
  removeUliAutoAllocationMessage(
    ctx: StateContext<ServerMessagesStateModel>,
    { payload }: ServerMessagesRemoveUliAutoAllocationMessage,
  ): void {
    const uliMessages: UliCreatedAndAllocatedServerMessageModel[] = ctx
      .getState()
      .uliTopicMessages.filter((item: UliCreatedAndAllocatedServerMessageModel) => item.uliNumber !== payload);

    ctx.patchState({ uliTopicMessages: uliMessages, highlightedUli: null });
  }

  @Action(ServerMessagesInitialState)
  setInitialState(ctx: StateContext<ServerMessagesInitialState>): void {
    ctx.setState(initialState);
  }
}
